import { parseSelectFilterOptions } from './search';

// URL param produced by the `productCategory` listing field (public enum,
// SelectMultipleFilter with searchMode 'has_any'). Values look like
// "has_any:Childrenswear" in location.search.
const PRODUCT_CATEGORY_PARAM = 'pub_productCategory';
// Item type (subcategory) param; publicData.productItemType is search-indexed.
const PRODUCT_ITEM_TYPE_PARAM = 'pub_productItemType';

/**
 * SEO copy per subcategory, keyed by the stored productItemType value
 * (see `value` in util/productTypes.js). Applies on
 * /s?pub_productCategory=<Category>&pub_productItemType=<value>.
 * Copy from Nick's Phase 2 brief (Oct 2026); no intro line was given.
 */
export const itemTypeSeo = {
  'buggies-prams-pushchairs': {
    title: 'Second-Hand Prams, Buggies & Pushchairs Ireland | ReBorn',
    description:
      'Shop second-hand prams, buggies and pushchairs in Ireland. Find quality preloved baby travel essentials at great prices, or sell yours on ReBorn.',
    h1: 'Second-Hand Prams, Buggies & Pushchairs in Ireland',
  },
  'travel-system-bundles': {
    title: 'Second-Hand Baby Travel Systems Ireland | ReBorn',
    description:
      'Browse preloved baby travel system bundles in Ireland. Discover second-hand pram and pushchair sets from sellers across Ireland, or list yours on ReBorn.',
    h1: 'Second-Hand Baby Travel System Bundles in Ireland',
  },
  'carriers-slings-travel-accessories': {
    title: 'Second-Hand Baby Carriers & Slings Ireland | ReBorn',
    description:
      'Shop preloved baby carriers, slings, wraps and travel accessories in Ireland. Discover second-hand essentials for families on the go, or sell yours on ReBorn.',
    h1: 'Second-Hand Baby Carriers & Travel Accessories in Ireland',
  },
};

/**
 * Hand-written SEO copy per product category. Only categories with bespoke copy
 * are listed here; any other category (e.g. Safety, Maternity, Pregnancy) keeps
 * the generic search title/description by design.
 *
 * Each entry powers four things on /s?pub_productCategory=<Category>:
 *   - title:       the <title> tag and JSON-LD page name
 *   - description: the <meta name="description"> and JSON-LD description
 *   - h1:          the on-page <h1>
 *   - intro:       the paragraph directly under the <h1>
 *
 * The category keys MUST match the `category` values in util/productTypes.js.
 */
export const categorySeo = {
  Childrenswear: {
    title: "Second Hand Children's Clothes in Ireland | ReBorn",
    description:
      "Browse second hand children's clothes from parents across Ireland. Find baby grows, clothing bundles and more at great value.",
    h1: "Second Hand Children's Clothes in Ireland",
    intro: "Browse preloved baby and children's clothing from parents across Ireland.",
  },
  Travel: {
    title: 'Used Prams, Buggies & Baby Carriers in Ireland | ReBorn',
    description:
      'Browse used prams, buggies and baby carriers from parents across Ireland. Save on quality baby travel items.',
    h1: 'Used Prams, Buggies & Baby Carriers in Ireland',
    intro: 'Find quality prams, buggies and baby carriers from parents across Ireland.',
  },
  Sleep: {
    title: 'Baby Sleep Products, Cots & Sleep Pods in Ireland | ReBorn',
    description:
      'Discover baby sleep products including cots, cribs and sleep pods from parents across Ireland.',
    h1: 'Baby Sleep Products, Cots & Sleep Pods in Ireland',
    intro: 'Browse cots, cribs and sleep essentials from parents across Ireland.',
  },
  Nursery: {
    title: 'Baby Furniture & Nursery Items in Ireland | ReBorn',
    description:
      'Shop baby furniture and nursery items from parents across Ireland. Find cots, storage and essentials at great value.',
    h1: 'Baby Furniture & Nursery Items in Ireland',
    intro: 'Shop nursery furniture and essentials from parents across Ireland.',
  },
  Feeding: {
    title: 'Baby Feeding Products & Accessories in Ireland | ReBorn',
    description:
      'Browse baby feeding products including bottles, pumps and high chairs from parents across Ireland.',
    h1: 'Baby Feeding Products & Accessories in Ireland',
    intro: 'Discover bottles, pumps and feeding essentials from parents across Ireland.',
  },
  Play: {
    title: 'Baby & Kids Toys and Play Items in Ireland | ReBorn',
    description:
      'Discover toys and play items for babies and children from parents across Ireland.',
    h1: 'Baby & Kids Toys and Play Items in Ireland',
    intro: 'Explore toys and play items for babies and children from parents across Ireland.',
  },
};

/**
 * Resolve the SEO copy for the current search page, or null when the page should
 * keep the generic search title/description.
 *
 * Per product decision, category SEO is applied only when ALL of the following hold:
 *   1. there is no active keyword search (keyword pages stay generic),
 *   2. exactly ONE productCategory is selected (mixed-category pages stay generic),
 *   3. that single category has hand-written copy in `categorySeo`.
 *
 * @param {Object} searchParams parsed URL search params (e.g. searchParamsInURL)
 * @returns {Object|null} the matching categorySeo entry, or null
 */
export const getActiveCategorySeo = searchParams => {
  if (!searchParams) {
    return null;
  }

  // Keyword searches should keep the keyword-based generic title.
  if (searchParams.keywords) {
    return null;
  }

  const rawValue = searchParams[PRODUCT_CATEGORY_PARAM];
  if (typeof rawValue !== 'string' || rawValue.length === 0) {
    return null;
  }

  // Strips the "has_any:" prefix and splits comma-separated values.
  const selectedCategories = parseSelectFilterOptions(rawValue);
  if (selectedCategories.length !== 1) {
    return null;
  }

  // A single selected subcategory with its own copy wins over the category copy.
  const rawItemType = searchParams[PRODUCT_ITEM_TYPE_PARAM];
  if (typeof rawItemType === 'string' && rawItemType.length > 0) {
    const selectedItemTypes = parseSelectFilterOptions(rawItemType);
    if (selectedItemTypes.length === 1 && itemTypeSeo[selectedItemTypes[0]]) {
      return itemTypeSeo[selectedItemTypes[0]];
    }
  }

  return categorySeo[selectedCategories[0]] || null;
};
